// SPDX-License-Identifier: AGPL-3.0-or-later

import AttachmentUrlRefresher from '@app/features/messaging/state/AttachmentUrlRefresher';
import * as ImageCacheUtils from '@app/features/messaging/utils/ImageCacheUtils';
import {decodeThumbHashDataURL} from '@app/features/messaging/utils/ThumbHashUtils';
import {type SyntheticEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';

type MediaLoadingElement = HTMLImageElement | HTMLVideoElement;
type MediaElementStatus = 'loaded' | 'error' | 'pending';

export const MEDIA_LOAD_RETRY_LIMIT = 2;
export const MEDIA_LOAD_RETRY_DELAY_MS = 1000;

export interface MediaLoadingState {
	loaded: boolean;
	error: boolean;
	cached: boolean;
	cachedOnMount: boolean;
	thumbHashURL?: string;
	ref: (element: MediaLoadingElement | null) => void;
	onLoad: (event: SyntheticEvent<MediaLoadingElement>) => void;
	onError: (event: SyntheticEvent<MediaLoadingElement>) => void;
}

interface UseMediaLoadingOptions {
	enabled?: boolean;
}

interface MediaLoadingFlags {
	loaded: boolean;
	error: boolean;
}

interface MediaLoadingSourceState extends MediaLoadingFlags {
	src: string;
	loadEnabled: boolean;
	cached: boolean;
}

interface MediaLoadingCacheAtMount {
	src: string;
	loadEnabled: boolean;
	cached: boolean;
}

interface MediaLoadingSourceIdentity {
	src: string;
	loadEnabled: boolean;
}

function isImageElement(element: MediaLoadingElement): element is HTMLImageElement {
	return element.tagName === 'IMG';
}

function getMediaElementStatus(element: MediaLoadingElement | null): MediaElementStatus {
	if (!element) return 'pending';
	if (isImageElement(element)) {
		if (!element.currentSrc && !element.src) return 'pending';
		if (element.complete && element.naturalWidth > 0) return 'loaded';
		if (element.complete) return 'error';
		return 'pending';
	}
	if (element.error) return 'error';
	return element.readyState >= 2 ? 'loaded' : 'pending';
}

function mediaElementMatchesSource(element: MediaLoadingElement | null, src: string): boolean {
	if (!element || src.length === 0) return false;
	let resolvedSource = src;
	try {
		resolvedSource = new URL(src, element.ownerDocument.baseURI).href;
	} catch {
		resolvedSource = src;
	}
	const matchesResolvedSource = (value: string): boolean => {
		if (value === src || value === resolvedSource) return true;
		try {
			return new URL(value, element.ownerDocument.baseURI).href === resolvedSource;
		} catch {
			return false;
		}
	};
	const attributeSource = element.getAttribute('src');
	if (attributeSource != null && !matchesResolvedSource(attributeSource)) return false;
	if (element.src.length > 0 && !matchesResolvedSource(element.src)) return false;
	if (element.currentSrc.length > 0 && !matchesResolvedSource(element.currentSrc)) return false;
	return attributeSource != null || element.src.length > 0 || element.currentSrc.length > 0;
}

export function createMediaLoadingSourceState(
	src: string,
	loadEnabled: boolean,
	cached: boolean,
): MediaLoadingSourceState {
	return {
		src,
		loadEnabled,
		cached,
		loaded: cached,
		error: false,
	};
}

export function resolveMediaLoadingSourceState(
	state: MediaLoadingSourceState,
	src: string,
	loadEnabled: boolean,
	cached: boolean,
): MediaLoadingSourceState {
	if (state.src === src && state.loadEnabled === loadEnabled) return state;
	if (!loadEnabled) {
		return pendingMediaLoadingState(src, loadEnabled);
	}
	if (state.loaded && state.loadEnabled) {
		return {src, loadEnabled, cached, loaded: true, error: false};
	}
	return createMediaLoadingSourceState(src, loadEnabled, cached);
}

export function shouldRetryMediaLoad(failedAttempts: number): boolean {
	return failedAttempts < MEDIA_LOAD_RETRY_LIMIT;
}

function sourceIdentityMatches(
	currentIdentity: MediaLoadingSourceIdentity,
	expectedIdentity: MediaLoadingSourceIdentity,
): boolean {
	return currentIdentity.src === expectedIdentity.src && currentIdentity.loadEnabled === expectedIdentity.loadEnabled;
}

function pendingMediaLoadingState(src: string, loadEnabled: boolean): MediaLoadingSourceState {
	return {src, loadEnabled, cached: false, loaded: false, error: false};
}

function reloadMediaElement(element: MediaLoadingElement, src: string): void {
	if (isImageElement(element)) {
		element.removeAttribute('src');
		element.src = src;
		return;
	}
	element.src = src;
}

export function useMediaLoading(
	src: string,
	placeholder?: string,
	options: UseMediaLoadingOptions = {},
): MediaLoadingState {
	const {enabled = true} = options;
	const mediaElementRef = useRef<MediaLoadingElement | null>(null);
	const refreshedSourceRef = useRef<string | null>(null);
	const retryAttemptRef = useRef(0);
	const retryTimeoutRef = useRef(0);
	const retryPendingRef = useRef(false);
	const loadEnabled = enabled && src.length > 0;
	const sourceIdentity = useMemo<MediaLoadingSourceIdentity>(() => ({src, loadEnabled}), [loadEnabled, src]);
	const committedSourceIdentityRef = useRef(sourceIdentity);
	const retryIdentityRef = useRef(sourceIdentity);
	const initialCached = loadEnabled && ImageCacheUtils.hasImage(src);
	const [cacheAtMount] = useState<MediaLoadingCacheAtMount>(() => ({
		src,
		loadEnabled,
		cached: initialCached,
	}));
	const cachedOnMount = cacheAtMount.src === src && cacheAtMount.loadEnabled === loadEnabled && cacheAtMount.cached;
	const [sourceState, setSourceState] = useState<MediaLoadingSourceState>(() =>
		createMediaLoadingSourceState(src, loadEnabled, initialCached),
	);
	const currentSourceState = resolveMediaLoadingSourceState(sourceState, src, loadEnabled, initialCached);
	const thumbHashURL = useMemo(() => {
		return decodeThumbHashDataURL(placeholder);
	}, [placeholder]);
	const clearRetryTimeout = useCallback(() => {
		if (retryTimeoutRef.current === 0) return;
		window.clearTimeout(retryTimeoutRef.current);
		retryTimeoutRef.current = 0;
	}, []);
	const applyLoaded = useCallback(
		(element: MediaLoadingElement) => {
			if (!sourceIdentityMatches(committedSourceIdentityRef.current, sourceIdentity)) return;
			if (mediaElementRef.current !== element) return;
			if (!mediaElementMatchesSource(element, src)) return;
			retryAttemptRef.current = 0;
			retryPendingRef.current = false;
			clearRetryTimeout();
			if (isImageElement(element)) ImageCacheUtils.rememberImage(src, element);
			setSourceState({src, loadEnabled, cached: true, loaded: true, error: false});
		},
		[clearRetryTimeout, loadEnabled, sourceIdentity, src],
	);
	const scheduleReload = useCallback(() => {
		clearRetryTimeout();
		retryTimeoutRef.current = window.setTimeout(() => {
			retryTimeoutRef.current = 0;
			retryPendingRef.current = false;
			if (!sourceIdentityMatches(committedSourceIdentityRef.current, sourceIdentity)) return;
			const element = mediaElementRef.current;
			if (element == null || !loadEnabled) return;
			reloadMediaElement(element, src);
		}, MEDIA_LOAD_RETRY_DELAY_MS);
	}, [clearRetryTimeout, loadEnabled, sourceIdentity, src]);
	const applyError = useCallback(
		(element: MediaLoadingElement) => {
			if (!sourceIdentityMatches(committedSourceIdentityRef.current, sourceIdentity)) return;
			if (mediaElementRef.current !== element) return;
			if (!mediaElementMatchesSource(element, src)) return;
			if (retryPendingRef.current) return;
			ImageCacheUtils.forgetImage(src);
			// A retry of the same URL is pointless if the URL itself expired, so force one refresh
			// before the retries run. Once per source: a refresh that fails must not loop.
			if (refreshedSourceRef.current !== src) {
				refreshedSourceRef.current = src;
				void AttachmentUrlRefresher.refresh(src, {force: true});
			}
			if (shouldRetryMediaLoad(retryAttemptRef.current)) {
				retryAttemptRef.current += 1;
				retryPendingRef.current = true;
				setSourceState((currentState) => {
					if (currentState.src !== src || currentState.loadEnabled !== loadEnabled) return currentState;
					return {...currentState, cached: false, error: false};
				});
				scheduleReload();
				return;
			}
			setSourceState({src, loadEnabled, cached: false, loaded: false, error: true});
		},
		[loadEnabled, scheduleReload, sourceIdentity, src],
	);
	const probeMediaElement = useCallback(() => {
		if (!loadEnabled) return;
		const element = mediaElementRef.current;
		if (!mediaElementMatchesSource(element, src) || element == null) return;
		const status = getMediaElementStatus(element);
		if (status === 'loaded') {
			applyLoaded(element);
			return;
		}
		if (status === 'error') {
			applyError(element);
		}
	}, [applyError, applyLoaded, loadEnabled, src]);
	const ref = useCallback((element: MediaLoadingElement | null) => {
		mediaElementRef.current = element;
	}, []);
	useLayoutEffect(() => {
		committedSourceIdentityRef.current = sourceIdentity;
		if (!sourceIdentityMatches(retryIdentityRef.current, sourceIdentity)) {
			retryAttemptRef.current = 0;
			retryPendingRef.current = false;
			retryIdentityRef.current = sourceIdentity;
			clearRetryTimeout();
		}
		if (!loadEnabled) {
			setSourceState(pendingMediaLoadingState(src, loadEnabled));
			return;
		}
		const element = mediaElementRef.current;
		if (mediaElementMatchesSource(element, src) && element != null) {
			const status = getMediaElementStatus(element);
			if (status === 'loaded') {
				if (isImageElement(element)) ImageCacheUtils.rememberImage(src, element);
				setSourceState({src, loadEnabled, cached: true, loaded: true, error: false});
				return;
			}
			if (status === 'error') {
				applyError(element);
				return;
			}
		}
		const cached = ImageCacheUtils.hasImage(src);
		setSourceState((currentState) => resolveMediaLoadingSourceState(currentState, src, loadEnabled, cached));
	}, [applyError, clearRetryTimeout, loadEnabled, sourceIdentity, src]);
	useLayoutEffect(() => {
		const element = mediaElementRef.current;
		if (element == null || !loadEnabled) return;
		const handleLoad = () => applyLoaded(element);
		const handleError = () => applyError(element);
		element.addEventListener('load', handleLoad);
		element.addEventListener('error', handleError);
		return () => {
			element.removeEventListener('load', handleLoad);
			element.removeEventListener('error', handleError);
		};
	}, [applyError, applyLoaded, loadEnabled, src]);
	useEffect(() => {
		if (!loadEnabled) return;
		const handlePageShow = () => probeMediaElement();
		const handleVisibility = () => {
			if (document.visibilityState === 'visible') probeMediaElement();
		};
		window.addEventListener('pageshow', handlePageShow);
		document.addEventListener('visibilitychange', handleVisibility);
		return () => {
			window.removeEventListener('pageshow', handlePageShow);
			document.removeEventListener('visibilitychange', handleVisibility);
		};
	}, [loadEnabled, probeMediaElement]);
	useEffect(() => () => clearRetryTimeout(), [clearRetryTimeout]);
	const onLoad = useCallback(
		(event: SyntheticEvent<MediaLoadingElement>) => {
			applyLoaded(event.currentTarget);
		},
		[applyLoaded],
	);
	const onError = useCallback(
		(event: SyntheticEvent<MediaLoadingElement>) => {
			applyError(event.currentTarget);
		},
		[applyError],
	);
	return {
		loaded: currentSourceState.loaded,
		error: currentSourceState.error,
		cached: currentSourceState.cached,
		cachedOnMount,
		thumbHashURL,
		ref,
		onLoad,
		onError,
	};
}
